import { isValidElement, ReactNode } from 'react';
import { Id, toast, ToastContent, ToastItem, ToastOptions } from 'react-toastify';
import { isString } from 'lodash';
import { logger } from '@cutwise/utils';

import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import AppError from '@/common/state/AppError';

interface NotificationMessageParams {
  delay?: number;
  toastId?: Nullable<Id>;
  isClosedOnClick?: boolean;
  onRemove?: (toast: ToastItem) => void;
}

type ToastHandler = (content: ToastContent, options?: ToastOptions) => string;

type Message = ReactNode;

// todo: https://github.com/fkhadra/react-toastify/issues/402
export default class Notifier {
  private static idToInnerToastId: Record<string, string> = {};

  static success(message: Message, params: NotificationMessageParams = {}) {
    Notifier.sendNotification(toast.success as ToastHandler, message, params);
  }

  static warn(message: Message, params: NotificationMessageParams = {}) {
    Notifier.sendNotification(toast.warn as ToastHandler, message, params);
  }

  static error(message: Message | Error | AppError | { message?: string }, params: NotificationMessageParams = {}) {
    if (isValidElement(message) || isString(message)) {
      Notifier.sendErrorNotification(message, params);
    } else if (message instanceof AppError) {
      Notifier.sendErrorNotification(message.description || message.title, params);
    } else if (message instanceof Error) {
      logger.error(message);
      Notifier.sendErrorNotification(message.message, params);
    } else if (message && (message as Any).message) {
      Notifier.sendErrorNotification((message as Any).message, params);
    }

    // if message object do not contain message or title, do nothing
  }

  private static sendErrorNotification(message: Message, { delay = 0, ...rest }: NotificationMessageParams = {}) {
    if (!message) {
      return;
    }

    Notifier.sendNotification(toast.error as ToastHandler, message, { delay, ...rest });
  }

  private static sendNotification(
    sender: (content: ToastContent, options?: ToastOptions | undefined) => string,
    message: Message,
    params: NotificationMessageParams = {},
  ) {
    const { delay = 5000, toastId, isClosedOnClick = true, onRemove } = params;

    const options: ToastOptions = {
      autoClose: delay > 0 ? delay : false,
      position: 'top-right',
      hideProgressBar: true,
      toastId: toastId === null ? undefined : toastId,
      icon: false,
      closeOnClick: isClosedOnClick,
    };

    const content = <ToastNotification message={message} />;

    if (toastId && toast.isActive(Notifier.idToInnerToastId[toastId])) {
      toast.update(Notifier.idToInnerToastId[toastId], {
        ...options,
        render: content,
      });

      return;
    }

    const innerToastId = sender(content, options);

    if (toastId) {
      Notifier.idToInnerToastId[toastId] = innerToastId;
    }

    toast.onChange((toast: ToastItem) => {
      if (toastId && toastId === Notifier.idToInnerToastId[toastId] && toast.status === 'removed') {
        onRemove?.(toast);
      }
    });
  }

  static dismissNotification(toastId: string) {
    if (!Notifier.idToInnerToastId[toastId]) {
      return;
    }

    toast.dismiss(Notifier.idToInnerToastId[toastId]);
  }

  static dismissAll() {
    toast.dismiss();
  }
}
